import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import EngineerLayout from './EngineerLayout';
import { 
    FaEdit, 
    FaArrowLeft,
    FaSort,
    FaPrint,
    FaFileExcel,
    FaCopy,
    FaEye,
    FaUserTie,
    FaBriefcase,
    FaMoneyBillWave,
    FaSearch
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const EngineerSubheWorkList = () => {
    const navigate = useNavigate();
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Marathi numerals for display
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    useEffect(() => {
        setLoading(true);
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        
        onValue(engRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const flattened = Object.entries(data).map(([id, val]) => {
                    const generatedWorks = val.GeneratedLottery ? Object.values(val.GeneratedLottery) : [];
                    const workCount = generatedWorks.length;
                    const totalAmount = generatedWorks.reduce((sum, work) => sum + (parseFloat(work.tenderAmount || work.amount) || 0), 0);

                    return {
                        id,
                        ...val,
                        displayName: val.firstName ? `${val.firstName} ${val.surname}` : (val.fullName || val.name || "-"),
                        workCount,
                        totalAmount
                    };
                });
                setRegistrations(flattened.reverse());
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });
    }, []);

    const filteredData = useMemo(() => {
        return registrations.filter(item => {
            const hasWork = item.GeneratedLottery && Object.keys(item.GeneratedLottery).length > 0;
            const matchesSearch = item.displayName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                 (item.whatsappNo && item.whatsappNo.includes(searchTerm)) ||
                                 (item.mobile && item.mobile.includes(searchTerm));
            return hasWork && matchesSearch;
        });
    }, [registrations, searchTerm]);

    const downloadExcel = () => {
        const excelData = filteredData.map((item, index) => ({
            'अ.क्र.': index + 1,
            'अर्जदाराचे नाव': item.displayName,
            'फोन नंबर': item.whatsappNo || item.mobile || "-",
            'एकूण कामे': item.workCount,
            'एकूण रक्कम': item.totalAmount
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sub_Engineer_Work_List");
        XLSX.writeFile(wb, `Sub_Engineer_Work_List_${new Date().toLocaleDateString()}.xlsx`);
        toast.info("एक्सेल फाईल डाउनलोड होत आहे...");
    };

    const copyToClipboard = () => {
        const headers = ['अ.क्र.', 'अर्जदाराचे नाव', 'फोन नंबर', 'एकूण कामे', 'एकूण रक्कम'];
        const rows = filteredData.map((item, index) => [
            index + 1,
            item.displayName,
            item.whatsappNo || item.mobile || "-",
            item.workCount,
            item.totalAmount
        ]);

        const content = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(content);
        toast.success("माहिती कॉपी झाली!");
    };

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe] font-marathi">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                </div>
            </EngineerLayout>
        );
    }

    return (
        <EngineerLayout>
            <div className="p-8 bg-[#f4f7fe] min-h-screen font-marathi">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-8 px-2 no-print">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate('/engineer/work-passbook-menu')}
                            className="p-2.5 bg-white rounded-xl shadow-sm hover:translate-x-[-2px] transition text-gray-400 border border-gray-100 hover:text-blue-500"
                        >
                            <FaArrowLeft size={16} />
                        </button>
                        <h1 className="text-2xl font-bold text-gray-800 tracking-tight">सु.बे. अभियंत्यांची कामाची यादी (Work Passbook List)</h1>
                    </div>
                    <div className="text-[11px] font-black text-gray-400 uppercase tracking-widest bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-50">
                        ZP SANGLI / <span className="text-gray-600">WORK PASSBOOK</span>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    {/* Toolbar */}
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-10 no-print">
                        <div className="flex bg-[#6c757d] rounded overflow-hidden shadow-sm">
                            <button onClick={copyToClipboard} className="hover:bg-gray-700 text-white px-5 py-2 text-xs font-bold transition border-r border-gray-500/50 flex items-center gap-2">
                                <FaCopy size={12} /> कॉपी करा
                            </button>
                            <button onClick={downloadExcel} className="hover:bg-gray-700 text-white px-5 py-2 text-xs font-bold border-r border-gray-500/50 transition flex items-center gap-2">
                                <FaFileExcel size={12} /> एक्सेल
                            </button>
                            <button onClick={() => window.print()} className="hover:bg-gray-700 text-white px-5 py-2 text-xs font-bold transition flex items-center gap-2">
                                <FaPrint size={12} /> प्रिंट
                            </button>
                        </div>
                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <label className="text-sm font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">शोधा:</label>
                            <div className="relative w-full md:w-80">
                                <input 
                                    type="text" 
                                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold bg-gray-50/50 pr-10"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="नाव किंवा फोन नंबर..."
                                />
                                <FaSearch className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
                            </div>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-[#f8f9fc] border-b border-gray-100">
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-500 text-center uppercase tracking-wider w-20 border-r border-gray-50">#</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-500 text-left uppercase tracking-wider border-r border-gray-50 min-w-[250px]">अभियंत्याचे नाव</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-500 text-left uppercase tracking-wider border-r border-gray-50 w-44">संपर्क तपशील</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-500 text-center uppercase tracking-wider border-r border-gray-50 w-32">एकूण कामे</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-500 text-left uppercase tracking-wider border-r border-gray-50 w-40">एकूण रक्कम</th>
                                    <th className="px-6 py-5 text-[11px] font-black text-gray-500 text-center uppercase tracking-wider w-32 bg-gray-50/10">कृती</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredData.length > 0 ? filteredData.map((item, index) => (
                                    <tr key={item.id} className="hover:bg-blue-50/20 group transition-colors text-[13px] text-gray-600 font-bold">
                                        <td className="px-6 py-6 text-center border-r border-gray-50 text-gray-400">{toMarathiNumber(index + 1)}</td>
                                        <td className="px-6 py-6 border-r border-gray-50">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                                                    <FaUserTie size={16} />
                                                </div>
                                                <div className="text-gray-900 font-black leading-tight px-3">{item.displayName}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 border-r border-gray-50 text-gray-700">{toMarathiNumber(item.whatsappNo || item.mobile || "-")}</td>
                                        <td className="px-6 py-6 border-r border-gray-50 text-center">
                                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 text-amber-700 rounded-full border border-amber-100 text-[11px]">
                                                <FaBriefcase size={10} /> {toMarathiNumber(item.workCount)} कामे
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 border-r border-gray-50">
                                            <div className="flex items-center gap-2 text-emerald-600">
                                                <FaMoneyBillWave size={12} className="opacity-50" />
                                                ₹ {toMarathiNumber(item.totalAmount.toLocaleString('en-IN'))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 text-center bg-gray-50/10">
                                            <div className="flex items-center justify-center">
                                                <button 
                                                    onClick={() => navigate(`/engineer/subhe-work-detail/${item.id}`)}
                                                    className="p-3 bg-white border border-blue-50 rounded-xl text-blue-500 hover:bg-blue-500 hover:text-white hover:scale-110 transition shadow-sm group/eye"
                                                    title="पहा (View Details)"
                                                >
                                                    <FaEye size={16} className="group-hover/eye:animate-pulse" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-20 text-center text-gray-400 font-bold italic bg-gray-50/30 tracking-widest uppercase">
                                            कोणतीही माहिती उपलब्ध नाही.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                {/* Footer Copy */}
                <div className="mt-8 text-center text-[10px] text-gray-400 font-sans tracking-widest uppercase no-print">
                    Owned and Operated by ZP SANGLI. Copyright © 2026. All Rights Reserved.
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    .shadow-sm { box-shadow: none !important; }
                    .rounded-2xl { border-radius: 0 !important; }
                    .p-8 { padding: 0 !important; }
                    table { border: 1px solid #efefef !important; }
                    th { background: #f8f9fc !important; color: #555 !important; font-weight: 900 !important; -webkit-print-color-adjust: exact; }
                }
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSubheWorkList;
